import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, Plus, Trash2 } from "lucide-react";
import {
  api,
  type BillingFile,
  type BillingRun,
  type BillingSummaryRow,
  type Company,
  type ManualItem,
  type Page,
} from "@/lib/api";
import { dateTime } from "@/lib/format";
import {
  Badge,
  Button,
  Card,
  Combobox,
  ConfirmDialog,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  PageHeader,
  SortableTh,
  Spinner,
  Stat,
  Table,
  TableFooter,
  Tabs,
  Td,
  Th,
  Toolbar,
  type Tone,
} from "@/components/ui";

import { useClientTable } from "@/lib/table";

export const processes = [
  { id: "user", label: "Benutzer" },
  { id: "company", label: "Grundgebühr" },
  { id: "minimum_user", label: "Mindestabnahme" },
  { id: "manual", label: "Manuelle Positionen" },
];

export function BillingPage() {
  const [tab, setTab] = useState("runs");
  const [month, setMonth] = useState(previousMonth());

  return (
    <>
      <PageHeader
        title="Abrechnung"
        description="Was monatlich an A1 gemeldet wird — und was daraus geworden ist."
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "runs", label: "Läufe" },
          { id: "summary", label: "Monatsübersicht" },
          { id: "manual", label: "Manuelle Positionen" },
          { id: "files", label: "Archiv" },
        ]}
      />

      {tab === "runs" && <Runs month={month} onMonth={setMonth} />}
      {tab === "summary" && <Summary month={month} onMonth={setMonth} />}
      {tab === "manual" && <ManualItems />}
      {tab === "files" && <Files />}
    </>
  );
}

function previousMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function MonthPicker({
  month,
  onMonth,
}: {
  month: string;
  onMonth: (m: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="muted">Monat</span>
      <Input
        className="w-40"
        type="month"
        value={month}
        onChange={(e) => onMonth(e.target.value)}
      />
    </label>
  );
}

// --- runs ----------------------------------------------------------------

function Runs({ month, onMonth }: { month: string; onMonth: (m: string) => void }) {
  const qc = useQueryClient();
  const [preview, setPreview] = useState<{ process: string } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["billing", "runs"],
    queryFn: () => api<BillingRun[]>("/api/admin/billing/runs?months=12"),
  });

  const run = useMutation({
    mutationFn: (vars: { month: string; process: string }) =>
      api<BillingRun[]>("/api/admin/billing/run", { method: "POST", body: vars }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["billing"] }),
  });

  // Twelve months of four runs: forty-eight rows at most, so they order here.
  const table = useClientTable(data, {
    initial: { key: "month", dir: "desc" },
    pageSize: 25,
    accessors: {
      process: (r) => processLabel(r.process),
      state: (r) => runLabel(r),
      lastAttemptAt: (r) => r.lastAttemptAt ?? "",
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;

  const forMonth = (data ?? []).filter((r) => r.month === month);

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-4">
        <Toolbar>
          <MonthPicker month={month} onMonth={onMonth} />
          <div className="flex-1" />
          <Button onClick={() => run.mutate({ month, process: "" })} disabled={run.isPending}>
            <Play className="size-4" aria-hidden /> Alle vier Läufe starten
          </Button>
        </Toolbar>

        <Notice>
          Ein Monat, der bereits erfolgreich war, wird nicht noch einmal
          hochgeladen. Ein fehlgeschlagener Lauf sendet die einmal erzeugten
          Beträge erneut, nicht neu berechnete.
        </Notice>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {processes.map((p) => {
            const r = forMonth.find((x) => x.process === p.id);
            return (
              <Card key={p.id} className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium">{p.label}</div>
                    <div className="mt-1 text-xs muted">
                      {r ? `${r.records} Positionen` : "noch nicht gelaufen"}
                    </div>
                  </div>
                  {r && <Badge tone={runTone(r)}>{runLabel(r)}</Badge>}
                </div>

                {r?.lastError && (
                  <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                    {r.lastError}
                  </p>
                )}
                {r && r.amount > 0 && (
                  <p className="mt-2 text-sm tabular-nums">{euro(r.amount)}</p>
                )}

                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => setPreview({ process: p.id })}>
                    Probelauf
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={run.isPending || r?.state === "success"}
                    onClick={() => run.mutate({ month, process: p.id })}
                  >
                    Starten
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {run.error && (
          <div className="mt-4">
            <ErrorNote>{(run.error as Error).message}</ErrorNote>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h2 className="mb-3 font-medium">Die letzten zwölf Monate</h2>
        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="month" first="desc">
                Monat
              </SortableTh>
              <SortableTh table={table} sortKey="process">
                Art
              </SortableTh>
              <SortableTh table={table} sortKey="state">
                Zustand
              </SortableTh>
              <SortableTh table={table} sortKey="step">
                Schritt
              </SortableTh>
              <SortableTh table={table} sortKey="records" first="desc" align="right">
                Positionen
              </SortableTh>
              <SortableTh table={table} sortKey="amount" first="desc" align="right">
                Betrag
              </SortableTh>
              <SortableTh table={table} sortKey="uploadedAs">
                Datei
              </SortableTh>
              <SortableTh table={table} sortKey="lastAttemptAt" first="desc">
                Zuletzt
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r) => (
              <tr key={r.id}>
                <Td className="tabular-nums">{r.month}</Td>
                <Td>{processLabel(r.process)}</Td>
                <Td>
                  <Badge tone={runTone(r)}>{runLabel(r)}</Badge>
                </Td>
                <Td className="muted">{r.step || "—"}</Td>
                <Td className="text-right tabular-nums">{r.records}</Td>
                <Td className="text-right tabular-nums">{euro(r.amount)}</Td>
                <Td className="font-mono text-xs">{r.uploadedAs || "—"}</Td>
                <Td className="muted">{dateTime(r.lastAttemptAt)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {(data ?? []).length === 0 && <Empty title="Noch keine Läufe" />}
        <TableFooter table={table} noun="Läufe" />
      </Card>

      {preview && (
        <PreviewDialog
          month={month}
          process={preview.process}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}

function PreviewDialog({
  month,
  process,
  onClose,
}: {
  month: string;
  process: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["billing", "preview", month, process],
    queryFn: () =>
      api<{ month: string; process: string; count: number; records: unknown[] }>(
        `/api/admin/billing/preview?month=${month}&process=${process}`,
      ),
  });

  return (
    <Dialog open onClose={onClose} title={`Probelauf: ${processLabel(process)} ${month}`}>
      <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
        <Notice>
          Nichts wird geschrieben und nichts hochgeladen. Das ist genau die
          Datei, die der Lauf senden würde.
        </Notice>
        {isLoading && <Spinner />}
        {error && <ErrorNote>{(error as Error).message}</ErrorNote>}
        {data && (
          <>
            <p className="text-sm">{data.count} Positionen</p>
            <pre className="max-h-96 overflow-auto rounded bg-[var(--surface-sunken)] p-3 text-xs">
              {data.records
                .slice(0, 50)
                .map((r) => JSON.stringify(r))
                .join("\n")}
            </pre>
          </>
        )}
        <div className="flex justify-end">
          <Button onClick={onClose}>Schließen</Button>
        </div>
      </div>
    </Dialog>
  );
}

// --- summary -------------------------------------------------------------

function Summary({ month, onMonth }: { month: string; onMonth: (m: string) => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["billing", "summary", month],
    queryFn: () => api<BillingSummaryRow[]>(`/api/admin/billing/summary?month=${month}`),
  });

  const rows = data ?? [];
  // One row per customer, and the amounts are the point: every column sorts,
  // so "who is the largest invoice this month" needs no export.
  const table = useClientTable(rows, {
    initial: { key: "company", dir: "asc" },
    pageSize: 50,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;

  const total = rows.reduce((sum, r) => sum + r.total, 0);
  const users = rows.reduce((sum, r) => sum + r.users, 0);

  return (
    <div className="flex flex-col gap-4">
      <Toolbar>
        <MonthPicker month={month} onMonth={onMonth} />
      </Toolbar>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Firmen" value={rows.length} />
        <Stat label="Abgerechnete Benutzer" value={users} />
        <Stat label="Summe" value={euro(total)} />
      </div>

      <Card>
        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="company">
                Firma
              </SortableTh>
              <SortableTh table={table} sortKey="users" first="desc" align="right">
                Benutzer
              </SortableTh>
              <SortableTh table={table} sortKey="userTotal" first="desc" align="right">
                Benutzer €
              </SortableTh>
              <SortableTh table={table} sortKey="baseFee" first="desc" align="right">
                Grundgebühr
              </SortableTh>
              <SortableTh table={table} sortKey="minimum" first="desc" align="right">
                Mindestabnahme
              </SortableTh>
              <SortableTh table={table} sortKey="manual" first="desc" align="right">
                Manuell
              </SortableTh>
              <SortableTh table={table} sortKey="total" first="desc" align="right">
                Summe
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((r) => (
              <tr key={r.companyId}>
                <Td className="font-medium">{r.company}</Td>
                <Td className="text-right tabular-nums">{r.users}</Td>
                <Td className="text-right tabular-nums">{euro(r.userTotal)}</Td>
                <Td className="text-right tabular-nums">{euro(r.baseFee)}</Td>
                <Td className="text-right tabular-nums">{euro(r.minimum)}</Td>
                <Td className="text-right tabular-nums">{euro(r.manual)}</Td>
                <Td className="text-right font-medium tabular-nums">{euro(r.total)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {rows.length === 0 && <Empty title="Für diesen Monat wurde nichts abgerechnet" />}
        <TableFooter table={table} noun="Firmen" />
      </Card>
    </div>
  );
}

// --- manual items --------------------------------------------------------

function ManualItems() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<ManualItem | null>(null);
  const [openOnly, setOpenOnly] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ["billing", "manual", openOnly],
    queryFn: () => api<ManualItem[]>(`/api/admin/billing/manual?openOnly=${openOnly}`),
  });

  const companies = useQuery({
    queryKey: ["companies", "all"],
    queryFn: () => api<Page<Company>>("/api/admin/companies?limit=1000"),
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/billing/manual/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: ["billing", "manual"] });
    },
  });

  const table = useClientTable(data, {
    initial: { key: "month", dir: "desc" },
    pageSize: 25,
    resetOn: [openOnly],
    accessors: { billedAt: (m) => m.billedAt ?? "" },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <Card className="p-4">
        <Toolbar>
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={openOnly}
              onChange={(e) => setOpenOnly(e.target.checked)}
            />
            nur offene
          </label>
          <div className="flex-1" />
          <Button variant="primary" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden /> Position erfassen
          </Button>
        </Toolbar>

        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="month" first="desc">
                Monat
              </SortableTh>
              <SortableTh table={table} sortKey="company">
                Firma
              </SortableTh>
              <SortableTh table={table} sortKey="billingAccount">
                BAN
              </SortableTh>
              <SortableTh table={table} sortKey="description">
                Beschreibung
              </SortableTh>
              <SortableTh table={table} sortKey="amount" first="desc" align="right">
                Betrag
              </SortableTh>
              <SortableTh table={table} sortKey="billedAt" first="desc">
                Abgerechnet
              </SortableTh>
              <Th />
            </tr>
          </thead>
          <tbody>
            {table.rows.map((m) => (
              <tr key={m.id}>
                <Td className="tabular-nums">{m.month}</Td>
                <Td>{m.company}</Td>
                <Td className="tabular-nums">{m.billingAccount}</Td>
                <Td>{m.description}</Td>
                <Td className="text-right tabular-nums">{euro(m.amount)}</Td>
                <Td className="muted">{m.billedAt ? dateTime(m.billedAt) : "offen"}</Td>
                <Td className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!!m.billedAt}
                    title={m.billedAt ? "Bereits an A1 gemeldet" : undefined}
                    onClick={() => setRemoving(m)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {(data ?? []).length === 0 && <Empty title="Keine manuellen Positionen" />}
        <TableFooter table={table} noun="Positionen" />
      </Card>

      {adding && (
        <ManualItemDialog
          companies={companies.data?.items ?? []}
          onClose={() => setAdding(false)}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Position löschen"
        message={
          <>
            <strong>{removing?.description}</strong> über{" "}
            {removing ? euro(removing.amount) : ""} wird gelöscht.
          </>
        }
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
        busy={remove.isPending}
      />
    </>
  );
}

function ManualItemDialog({
  companies,
  onClose,
}: {
  companies: Company[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({
    companyId: companies[0]?.id ?? 0,
    billingAccount: "",
    month: previousMonth(),
    description: "",
    amount: 0,
  });

  const create = useMutation({
    mutationFn: () =>
      api<ManualItem>("/api/admin/billing/manual", { method: "POST", body: draft }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["billing", "manual"] });
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} title="Manuelle Position">
      <div className="flex flex-col gap-4">
        <Field label="Firma">
          <Combobox
            placeholder="Firma wählen"
            searchPlaceholder="Firma suchen …"
            emptyLabel="keine Firma gefunden"
            items={companies.map((c) => ({
              value: c.id,
              label: c.name,
              hint: c.domain,
            }))}
            value={draft.companyId}
            onChange={(v) => setDraft({ ...draft, companyId: Number(v) })}
          />
        </Field>
        <Field label="Monat">
          <Input
            type="month"
            value={draft.month}
            onChange={(e) => setDraft({ ...draft, month: e.target.value })}
          />
        </Field>
        <Field label="Beschreibung" hint="Steht so auf der Rechnung des Kunden.">
          <Input
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          />
        </Field>
        <Field label="Betrag">
          <Input
            type="number"
            step="0.01"
            value={draft.amount}
            onChange={(e) => setDraft({ ...draft, amount: Number(e.target.value) })}
          />
        </Field>
        <Field label="BAN" hint="Leer: der BAN der Firma wird verwendet.">
          <Input
            value={draft.billingAccount}
            onChange={(e) => setDraft({ ...draft, billingAccount: e.target.value })}
          />
        </Field>

        {create.error && <ErrorNote>{(create.error as Error).message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" disabled={create.isPending} onClick={() => create.mutate()}>
            Erfassen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// --- archive -------------------------------------------------------------

function Files() {
  const { data, isLoading } = useQuery({
    queryKey: ["billing", "files"],
    queryFn: () => api<BillingFile[]>("/api/admin/billing/files?limit=200"),
  });

  const table = useClientTable(data, {
    initial: { key: "at", dir: "desc" },
    pageSize: 25,
    accessors: {
      origin: (f) =>
        f.origin === "generated_for_a1"
          ? "an A1"
          : f.origin === "processed_from_a1"
            ? "von A1"
            : "unbekannt",
      process: (f) => (f.process ? processLabel(f.process) : ""),
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <SortableTh table={table} sortKey="filename">
              Datei
            </SortableTh>
            <SortableTh table={table} sortKey="origin">
              Richtung
            </SortableTh>
            <SortableTh table={table} sortKey="month" first="desc">
              Monat
            </SortableTh>
            <SortableTh table={table} sortKey="process">
              Art
            </SortableTh>
            <SortableTh table={table} sortKey="size" first="desc" align="right">
              Größe
            </SortableTh>
            <SortableTh table={table} sortKey="at" first="desc">
              Zeitpunkt
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {table.rows.map((f) => (
            <tr key={f.id}>
              <Td className="font-mono text-xs">{f.filename}</Td>
              <Td>
                {f.origin === "generated_for_a1" ? (
                  <Badge tone="info">an A1</Badge>
                ) : f.origin === "processed_from_a1" ? (
                  <Badge tone="success">von A1</Badge>
                ) : (
                  <Badge>unbekannt</Badge>
                )}
              </Td>
              <Td className="tabular-nums">{f.month || "—"}</Td>
              <Td>{f.process ? processLabel(f.process) : "—"}</Td>
              <Td className="text-right tabular-nums">{bytes(f.size)}</Td>
              <Td className="muted">{dateTime(f.at)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {(data ?? []).length === 0 && <Empty title="Noch keine Dateien ausgetauscht" />}
      <TableFooter table={table} noun="Dateien" />
    </Card>
  );
}

// --- labels --------------------------------------------------------------

function processLabel(p: string): string {
  return processes.find((x) => x.id === p)?.label ?? p;
}

function runTone(r: BillingRun): Tone {
  if (r.state === "success") return "success";
  if (r.state === "escalated") return "danger";
  return r.attempts > 0 ? "warning" : "info";
}

function runLabel(r: BillingRun): string {
  switch (r.state) {
    case "success":
      return r.step === "no_data" ? "nichts zu melden" : "erledigt";
    case "escalated":
      return "braucht einen Menschen";
    default:
      return r.attempts > 0 ? `Versuch ${r.attempts}/${r.maxAttempts}` : "offen";
  }
}

export function euro(v: number): string {
  return new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(v);
}

function bytes(v: number): string {
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${Math.round(v / 1024)} kB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
}
